import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Card, CardBody, Button } from 'reactstrap';
import { getSpecialtyFields } from 'config/subscriptionPlans';
import { getSpecialty } from 'config/specialtyConfig';
import { switchSpecialty } from 'redux/Specialty/SpecialtyAction';

const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const CurrentSubscriptionCard = ({ userDetails, activeSpecialty = 'pmr', overrideSpecialty }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const specialtyId = overrideSpecialty || activeSpecialty;
    const fields = getSpecialtyFields(specialtyId);

    const getStatus = () => {
        if (!userDetails) return null;

        if (userDetails[fields.lifetimeAccess] === true || userDetails[fields.lifetimeAccess] === 'true') {
            return {
                label: 'Lifetime Access',
                sub: 'Full permanent access',
                icon: 'fas fa-infinity',
                color: 'success',
                gradient: 'bg-gradient-success',
            };
        }

        if (userDetails[fields.ultimateAdvisingStudent] === true || userDetails[fields.ultimateAdvisingStudent] === 'true') {
            return {
                label: 'Ultimate Advising Student',
                sub: 'Full lifetime access',
                icon: 'fas fa-star',
                color: 'success',
                gradient: 'bg-gradient-success',
            };
        }

        if (userDetails[fields.subscriptionEndDate]) {
            const endDate = new Date(userDetails[fields.subscriptionEndDate]);
            const startDate = userDetails[fields.subscriptionStartDate] ? new Date(userDetails[fields.subscriptionStartDate]) : null;
            const now = new Date();
            const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

            if (now < endDate) {
                const totalDays = startDate
                    ? Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)))
                    : daysRemaining;
                const percent = Math.min(100, Math.max(0, (daysRemaining / totalDays) * 100));
                const warning = daysRemaining <= 30;
                return {
                    label: `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`,
                    sub: `Active subscription • Expires ${formatDate(endDate)}`,
                    icon: 'fas fa-check-circle',
                    color: warning ? 'warning' : 'success',
                    gradient: warning ? 'bg-gradient-warning' : 'bg-gradient-success',
                    startDate,
                    endDate,
                    percent,
                    action: {
                        label: 'Extend Subscription',
                        color: 'warning',
                        icon: 'fas fa-crown',
                    },
                };
            }
            return {
                label: 'Subscription Expired',
                sub: `Ended ${formatDate(endDate)}`,
                icon: 'fas fa-exclamation-circle',
                color: 'danger',
                gradient: 'bg-gradient-danger',
                action: { label: 'Extend Subscription', color: 'success', icon: 'fas fa-crown' },
            };
        }

        if (userDetails[fields.trialStartDate]) {
            const trialStart = new Date(userDetails[fields.trialStartDate]);
            const trialEnd = new Date(trialStart);
            trialEnd.setDate(trialEnd.getDate() + 14);
            const now = new Date();
            const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));

            if (now < trialEnd) {
                const percent = Math.min(100, Math.max(0, (daysRemaining / 14) * 100));
                const warning = daysRemaining <= 7;
                return {
                    label: `Free Trial • ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`,
                    sub: `Ends ${formatDate(trialEnd)}`,
                    icon: 'fas fa-gift',
                    color: warning ? 'warning' : 'info',
                    gradient: warning ? 'bg-gradient-warning' : 'bg-gradient-info',
                    startDate: trialStart,
                    endDate: trialEnd,
                    percent,
                    action: { label: 'Upgrade Now', color: 'warning', icon: 'fas fa-crown' },
                };
            }
            return {
                label: 'Trial Expired',
                sub: `Ended ${formatDate(trialEnd)}`,
                icon: 'fas fa-exclamation-circle',
                color: 'danger',
                gradient: 'bg-gradient-danger',
                action: { label: 'Get Full Access', color: 'success', icon: 'fas fa-crown' },
            };
        }

        return {
            label: 'No Active Plan',
            sub: 'Start your free trial or subscribe',
            icon: 'fas fa-lock',
            color: 'muted',
            gradient: 'bg-gradient-default',
            action: { label: 'View Plans', color: 'success', icon: 'fas fa-crown' },
        };
    };

    const status = getStatus();
    if (!status) return null;

    return (
        <Card className="shadow border-0" style={{ overflow: 'hidden' }}>
            <div className={status.gradient} style={{ height: '4px' }} />
            <CardBody className="p-4">
                <div className="d-flex align-items-center flex-wrap">
                    <div
                        className={`icon icon-shape ${status.gradient} text-white rounded-circle shadow mr-3`}
                        style={{ width: '64px', height: '64px', minWidth: '64px' }}
                    >
                        <i className={status.icon} style={{ fontSize: '1.75rem' }} />
                    </div>
                    <div className="flex-grow-1 mr-3" style={{ minWidth: 0 }}>
                        <h6
                            className="text-uppercase text-muted mb-1 font-weight-bold"
                            style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}
                        >
                            <i className={`${getSpecialty(specialtyId).icon} mr-1`} />
                            {getSpecialty(specialtyId).name} — Subscription Status
                        </h6>
                        <h2 className={`mb-1 text-${status.color}`} style={{ lineHeight: 1.2 }}>
                            {status.label}
                        </h2>
                        <p className="text-muted mb-0 small">{status.sub}</p>
                    </div>
                    {status.action && (
                        <Button
                            color={status.action.color}
                            className="mt-2 mt-sm-0"
                            onClick={(e) => {
                                e.preventDefault();
                                if (specialtyId !== activeSpecialty) {
                                    dispatch(switchSpecialty(specialtyId));
                                }
                                history.push('/client/plans');
                            }}
                        >
                            <i className={`${status.action.icon} mr-1`} />
                            {status.action.label}
                        </Button>
                    )}
                </div>

            </CardBody>
        </Card>
    );
};

const mapStateToProps = state => ({
    userDetails: state.loginAuth.userDetails,
    activeSpecialty: state.specialty.activeSpecialty,
});

export default connect(mapStateToProps)(CurrentSubscriptionCard);
